import { useState, type ComponentType } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Wallet, TrendingUp, Calendar, Package, Users, Gift } from "lucide-react";
import { api, SalesSummary } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { InfoTip } from "@/components/ui/tooltip";
import DailyTrendChart from "@/components/DailyTrendChart";
import StatsExplainer from "@/components/StatsExplainer";
import { formatPLN, cn } from "@/lib/utils";
import type { MetricTipId } from "@/lib/metricsGlossary";
import SiteFunnel from "@/components/SiteFunnel";
import JourneyFunnels from "@/components/JourneyFunnels";
import YoutubeFunnelPanel from "@/components/YoutubeFunnelPanel";
import JourneySections from "@/components/JourneySections";
import JourneyProductFunnels from "@/components/JourneyProductFunnels";
import JourneyShoppingPaths from "@/components/JourneyShoppingPaths";
import ShoppingSessionAnalysis from "@/components/ShoppingSessionAnalysis";
import DayTrafficAnalysis from "@/components/DayTrafficAnalysis";
import ContentSalesTimeline from "@/components/ContentSalesTimeline";
import FunnelTrendsPanel from "@/components/FunnelTrendsPanel";
import OpsEvents from "@/components/OpsEvents";
import ContentPurchaseLinksPanel from "@/components/ContentPurchaseLinksPanel";

export default function Sprzedaz() {
  const { data } = useQuery({
    queryKey: ["sales-summary"],
    queryFn: api.salesSummary,
  });

  const summary = data ?? {
    dzis_pln: 0,
    dni7_pln: 0,
    dni30_pln: 0,
    ytd_pln: 0,
    paid_orders_30d: 0,
    free_orders_30d: 0,
    customers_30d: 0,
    per_product: {},
    transactions: [],
  };

  return (
    <div>
      <PageHeader
        title="Sprzedaż"
        subtitle="Ebook 1, książka papierowa, wkrótce ebook 2"
      />

      <Tabs defaultValue="sprzedaz" className="mt-2">
        <TabsList>
          <TabsTrigger value="sprzedaz">Sprzedaż</TabsTrigger>
          <TabsTrigger value="zdarzenia">Zdarzenia</TabsTrigger>
          <TabsTrigger value="dzien">Ruch dnia</TabsTrigger>
          <TabsTrigger value="rolki">Rolki vs sprzedaż</TabsTrigger>
          <TabsTrigger value="trendy">Trendy lejka</TabsTrigger>
          <TabsTrigger value="powiazania">Treść ↔ zakupy</TabsTrigger>
          <TabsTrigger value="strona">Strona i lejek</TabsTrigger>
          <TabsTrigger value="lejki">Lejki</TabsTrigger>
          <TabsTrigger value="yt">YT</TabsTrigger>
          <TabsTrigger value="koszyki">Koszyki</TabsTrigger>
          <TabsTrigger value="sesje">Sesje zakupowe</TabsTrigger>
          <TabsTrigger value="sciezki">Ścieżki</TabsTrigger>
          <TabsTrigger value="sekcje">Sekcje</TabsTrigger>
        </TabsList>
        <TabsContent value="sprzedaz">
          <SprzedazTab summary={summary} />
        </TabsContent>
        <TabsContent value="zdarzenia">
          <OpsEvents />
        </TabsContent>
        <TabsContent value="dzien">
          <DayTrafficAnalysis />
        </TabsContent>
        <TabsContent value="rolki">
          <ContentSalesTimeline />
        </TabsContent>
        <TabsContent value="trendy">
          <FunnelTrendsPanel />
        </TabsContent>
        <TabsContent value="powiazania">
          <ContentPurchaseLinksPanel />
        </TabsContent>
        <TabsContent value="strona">
          <SiteFunnel />
        </TabsContent>
        <TabsContent value="lejki">
          <JourneyFunnels />
        </TabsContent>
        <TabsContent value="yt">
          <YoutubeFunnelPanel />
        </TabsContent>
        <TabsContent value="koszyki">
          <JourneyProductFunnels />
        </TabsContent>
        <TabsContent value="sesje">
          <ShoppingSessionAnalysis />
        </TabsContent>
        <TabsContent value="sciezki">
          <JourneyShoppingPaths />
        </TabsContent>
        <TabsContent value="sekcje">
          <JourneySections />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function SprzedazTab({ summary }: { summary: SalesSummary }) {
  const [days, setDays] = useState<7 | 30 | 90>(30);
  const dailyQuery = useQuery({
    queryKey: ["sales-daily", days],
    queryFn: () => api.salesDaily(days),
  });
  const series = dailyQuery.data?.series ?? [];

  return (
    <div className="grid gap-5">
      <StatsExplainer variant="sprzedaz" />

      <div className="grid gap-4 md:grid-cols-4">
        <Kpi title="Dziś" tipId="kpi_today" value={summary.dzis_pln} icon={Wallet} delay={0} />
        <Kpi
          title="Ostatnie 7 dni"
          tipId="kpi_period"
          value={summary.dni7_pln}
          icon={Calendar}
          delay={0.05}
        />
        <Kpi
          title="Ostatnie 30 dni"
          tipId="kpi_period"
          value={summary.dni30_pln}
          icon={TrendingUp}
          delay={0.1}
        />
        <Kpi title="YTD 2026" tipId="kpi_period" value={summary.ytd_pln} icon={Package} delay={0.15} />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <KpiCount
          title="Udane transakcje (30d)"
          tipId="paid_orders"
          value={summary.paid_orders_30d}
          icon={TrendingUp}
          delay={0.18}
        />
        <KpiCount
          title="Klienci (30d)"
          tipId="customers"
          value={summary.customers_30d}
          icon={Users}
          delay={0.2}
        />
        <KpiCount
          title="Darmowe produkty (30d)"
          tipId="free_orders"
          value={summary.free_orders_30d}
          icon={Gift}
          delay={0.22}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted-foreground">Okres wykresu:</span>
        {([7, 30, 90] as const).map((d) => (
          <Button
            key={d}
            size="sm"
            variant={days === d ? "default" : "outline"}
            onClick={() => setDays(d)}
          >
            {d} dni
          </Button>
        ))}
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <DailyTrendChart
            title="Trend sprzedaży dzień po dniu"
            subtitle="Źródło: EasyCart. Słupki = przychód (zł), linia opcjonalnie w danych. Anulowane poza sumą."
            data={series.map((r) => ({
              date: r.date,
              revenue: r.revenue,
              orders: r.paid_orders,
            }))}
            series={[
              {
                key: "revenue",
                label: "Przychód",
                color: "hsl(var(--brand))",
                format: "pln",
              },
              {
                key: "orders",
                label: "Zamówienia",
                color: "hsl(var(--primary))",
                rightAxis: true,
              },
            ]}
            mode="bar"
            hideForSingleDay={false}
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <Card className="h-full">
            <CardHeader>
              <CardTitle>Rozbicie po produktach</CardTitle>
              <p className="text-xs text-muted-foreground">Źródło: EasyCart</p>
            </CardHeader>
            <CardContent>
              {Object.keys(summary.per_product).length === 0 ? (
                <EmptyState />
              ) : (
                <ul className="space-y-3">
                  {Object.entries(summary.per_product).map(([produkt, kwota]) => (
                    <li key={produkt} className="flex items-center justify-between gap-2">
                      <span className="text-sm">{produkt}</span>
                      <span className="shrink-0 font-mono text-sm font-medium tabular-nums">
                        {formatPLN(kwota)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card>
          <CardHeader>
            <CardTitle>Ostatnie transakcje</CardTitle>
            <p className="text-xs text-muted-foreground">Źródło: EasyCart</p>
          </CardHeader>
          <CardContent className="p-0">
            {summary.transactions.length === 0 ? (
              <div className="p-5">
                <EmptyState />
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border/50 text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-5 py-3 text-left font-medium">Data</th>
                    <th className="px-5 py-3 text-left font-medium">Produkt</th>
                    <th className="px-5 py-3 text-right font-medium">Kwota</th>
                    <th className="px-5 py-3 text-left font-medium">Kanał</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.transactions.map((t, i) => (
                    <tr
                      key={i}
                      className="border-b border-border/30 last:border-0 hover:bg-muted/30"
                    >
                      <td className="px-5 py-3 text-sm tabular-nums">{t.date}</td>
                      <td className="px-5 py-3 text-sm">{t.produkt}</td>
                      <td className="px-5 py-3 text-right font-mono text-sm tabular-nums">
                        {formatPLN(t.kwota)}
                      </td>
                      <td className="px-5 py-3 text-sm text-muted-foreground">
                        {t.kanal ?? "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

function Kpi({
  title,
  tipId,
  value,
  icon: Icon,
  delay,
}: {
  title: string;
  tipId: MetricTipId;
  value: number;
  icon: ComponentType<{ className?: string }>;
  delay: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
    >
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="flex items-center gap-1">
            {title}
            <InfoTip tipId={tipId} />
          </CardTitle>
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/10 text-brand">
            <Icon className="h-4 w-4" />
          </div>
        </CardHeader>
        <CardContent>
          <div
            className={cn(
              "text-2xl font-bold tabular-nums",
              value === 0 && "text-muted-foreground",
            )}
          >
            {formatPLN(value)}
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">Źródło: EasyCart</p>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function KpiCount({
  title,
  tipId,
  value,
  icon: Icon,
  delay,
}: {
  title: string;
  tipId: MetricTipId;
  value: number;
  icon: ComponentType<{ className?: string }>;
  delay: number;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
    >
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="flex items-center gap-1 text-sm">
            {title}
            <InfoTip tipId={tipId} />
          </CardTitle>
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/10 text-brand">
            <Icon className="h-4 w-4" />
          </div>
        </CardHeader>
        <CardContent>
          <div
            className={cn(
              "text-2xl font-bold tabular-nums",
              value === 0 && "text-muted-foreground",
            )}
          >
            {value}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="mb-2 text-sm text-muted-foreground">Brak danych</div>
    </div>
  );
}
